import { t } from 'i18next';
import { useMutation, useQuery } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { GetManyResult } from 'core/types';
import { uomService } from 'parts/services';
import { IUom, IUomConversion, IUomConversionListPayload } from 'parts/types';

export const useGetUoms = (filters?, enabled = true): GetManyResult<IUom> => {
  const { data, isError, isLoading } = useQuery(
    ['baseUoms', filters],
    ({ signal }) => uomService.getManyUoms(filters, signal),
    {
      keepPreviousData: true,
      enabled,
    }
  );

  return {
    records: data?.records ?? [],
    count: data?.count,
    isError,
    isLoading,
  };
};

export const useGetUomConversions = (
  requestPayload: IUomConversionListPayload,
  options: { enabled?: boolean; fetchAllPages?: boolean } = {}
) =>
  useInfiniteQuery<IUomConversion>(
    uomService.getManyUomConversions,
    'uomConversions',
    requestPayload,
    options
  );

export const useUpsertUomConversion = () => {
  const { errors } = useHandleErrors({
    success: ({ partUomConversion }) =>
      partUomConversion.id
        ? { message: t('messageUomConversionUpdatingSuccess') }
        : { message: t('messageUomConversionCreatingSuccess') },
    loading: ({ partUomConversion }) =>
      partUomConversion.id
        ? { message: t('messageUomConversionUpdatingInProgress') }
        : { message: t('messageUomConversionCreatingInProgress') },
    error: ({ partUomConversion }) =>
      partUomConversion.id
        ? { title: t('errorTitleUomConversionUpdating') }
        : { title: t('errorTitleUomConversionCreating') },
    position: NotificationPosition.FORM,
    invalidateQueries: ['uomConversions'],
  });

  const { mutateAsync } = useMutation(uomService.upsertUomConversion, {
    ...errors,
  });

  return {
    upsertUomConversion: mutateAsync,
  };
};

export const useRemoveUomConversions = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageUomConversionsRemovingSuccess') },
    loading: { message: t('messageUomConversionsRemovingInProgress') },
    error: { title: t('errorTitleUomConversionsRemoving') },
    position: NotificationPosition.DRAWER,
    invalidateQueries: ['uomConversions'],
  });

  const { mutateAsync } = useMutation(uomService.removeUomConversions, {
    ...errors,
  });

  return {
    removeUomConversions: mutateAsync,
  };
};
